// This file is auto-generated by TanStack Router
// For now, we'll create a manual route tree

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { RootLayout } from "./components/layout/RootLayout";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { MarketingLayout } from "./components/layout/MarketingLayout";
import { LoginPage } from "./routes/auth/login";
import { RegisterPage } from "./routes/auth/register";
import { ForgotPasswordPage } from "./routes/auth/forgot-password";
import { ResetPasswordPage } from "./routes/auth/reset-password";
import { VerifyEmailPage } from "./routes/auth/verify-email";
import { DashboardPage } from "./routes/dashboard/index";
import { DomainsPage } from "./routes/dashboard/domains";
import { ToolsPage } from "./routes/dashboard/tools";
import { SecurityPage } from "./routes/dashboard/security";
import { SettingsPage } from "./routes/dashboard/settings";
import { ReportsPage } from "./routes/dashboard/reports";
import { ReportDetailPage } from "./routes/dashboard/report-detail";
import { TeamPage } from "./routes/dashboard/team";
import { ProfilePage } from "./routes/dashboard/profile";
import { AdminPage } from "./routes/dashboard/admin";
import { AdminOrganizationsPage } from "./routes/dashboard/admin-organizations";
import { AdminOrganizationDetailPage } from "./routes/dashboard/admin-organization-detail";
import { AdminUsersPage } from "./routes/dashboard/admin-users";
import { AdminUserDetailPage } from "./routes/dashboard/admin-user-detail";
import { CheckoutSuccessPage } from "./routes/dashboard/checkout-success";
import { HomePage } from "./routes/marketing/home";
import { PublicToolsPage } from "./routes/marketing/tools";
import { PricingPage } from "./routes/marketing/pricing";
import { ResourcesPage } from "./routes/marketing/resources";
import { BlogPage } from "./routes/marketing/blog";
import { BlogPostPage } from "./routes/marketing/blog-post";
import { TermsPage } from "./routes/marketing/terms";
import { PrivacyPage } from "./routes/marketing/privacy";
import { CookiesPage } from "./routes/marketing/cookies";
import { AboutPage } from "./routes/marketing/about";
import { ResourceHubPage } from "./routes/marketing/resource-hub";
import { WhyEmailsGoToSpamGuide } from "./routes/marketing/guides/why-emails-go-to-spam";
import { WhatIsEmailAuthenticationGuide } from "./routes/marketing/guides/what-is-email-authentication";
import { ProtectFromSpoofingGuide } from "./routes/marketing/guides/protect-from-spoofing";
import { RecipientSecurityGatewaysGuide } from "./routes/marketing/guides/recipient-security-gateways";
import { WebsiteSecurityEmailDeliverabilityGuide } from "./routes/marketing/guides/website-security-email-deliverability";
import { FixingSecurityHeadersGuide } from "./routes/marketing/guides/fixing-security-headers";
import { SPFCheckerPage } from "./routes/marketing/tools/spf-checker";
import { DKIMCheckerPage } from "./routes/marketing/tools/dkim-checker";
import { DMARCCheckerPage } from "./routes/marketing/tools/dmarc-checker";
import { EmailAuthenticationCheckerPage } from "./routes/marketing/tools/email-authentication-checker";
import { WebsiteSecurityScannerPage } from "./routes/marketing/tools/website-security-scanner";
import { BlacklistCheckerPage } from "./routes/marketing/tools/blacklist-checker";
import { useAuthStore } from "./stores/auth";

// Root route
const rootRoute = createRootRoute({
  component: RootLayout,
});

// Marketing layout route
const marketingLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "marketing-layout",
  component: MarketingLayout,
});

// Marketing pages
const homeRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/",
  component: HomePage,
});

const publicToolsRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools",
  component: PublicToolsPage,
});

const pricingRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/pricing",
  component: PricingPage,
});

const resourcesRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/resources",
  component: ResourcesPage,
});

const blogRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/blog",
  component: BlogPage,
});

const blogPostRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/blog/$slug",
  component: BlogPostPage,
});

const termsRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/terms",
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const cookiesRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/cookies",
  component: CookiesPage,
});

const aboutRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/about",
  component: AboutPage,
});

const resourceHubRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/resource-hub",
  component: ResourceHubPage,
});

// Guide routes
const guideWhySpamRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/why-emails-go-to-spam",
  component: WhyEmailsGoToSpamGuide,
});

const guideEmailAuthRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/what-is-email-authentication",
  component: WhatIsEmailAuthenticationGuide,
});

const guideSpoofingRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/protect-from-spoofing",
  component: ProtectFromSpoofingGuide,
});

const guideSecurityGatewaysRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/recipient-security-gateways",
  component: RecipientSecurityGatewaysGuide,
});

const guideWebsiteSecurityRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/website-security-email-deliverability",
  component: WebsiteSecurityEmailDeliverabilityGuide,
});

const guideFixingHeadersRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/guides/fixing-security-headers",
  component: FixingSecurityHeadersGuide,
});

// Individual tool pages (SEO landing pages)
const spfCheckerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/spf-checker",
  component: SPFCheckerPage,
});

const dkimCheckerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/dkim-checker",
  component: DKIMCheckerPage,
});

const dmarcCheckerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/dmarc-checker",
  component: DMARCCheckerPage,
});

const emailAuthCheckerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/email-authentication-checker",
  component: EmailAuthenticationCheckerPage,
});

const websiteSecurityScannerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/website-security-scanner",
  component: WebsiteSecurityScannerPage,
});

const blacklistCheckerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/tools/blacklist-checker",
  component: BlacklistCheckerPage,
});

// Auth routes (with redirect guard for already-logged-in users)
const authRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  id: "auth",
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user) {
      throw redirect({ to: "/dashboard" });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/login",
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
});

// Register route outside auth guard (allows plan selection step after account creation)
const registerRoute = createRoute({
  getParentRoute: () => marketingLayoutRoute,
  path: "/register",
  component: RegisterPage,
});

// Dashboard routes (protected)
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "dashboard-layout",
  component: DashboardLayout,
  beforeLoad: () => {
    const { user, isLoading } = useAuthStore.getState();
    if (!isLoading && !user) {
      throw redirect({ to: "/login" });
    }
  },
});

// Search params type for domain filtering
type DomainFilterSearch = {
  domain?: string;
  days?: number;
  status?: string; // comma-separated: "compliant,forwarded,non_compliant,threat"
};

const dashboardRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/dashboard",
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>): DomainFilterSearch => ({
    domain: typeof search.domain === 'string' ? search.domain : undefined,
    days: typeof search.days === 'number' ? search.days : (typeof search.days === 'string' ? parseInt(search.days, 10) || 30 : 30),
  }),
});

const domainsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/domains",
  component: DomainsPage,
});

const dashboardToolsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/dashboard/tools",
  component: ToolsPage,
});

const dashboardSecurityRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/dashboard/security",
  component: SecurityPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

const teamRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/team",
  component: TeamPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/profile",
  component: ProfilePage,
});

const reportsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/reports",
  component: ReportsPage,
  validateSearch: (search: Record<string, unknown>): DomainFilterSearch => ({
    domain: typeof search.domain === 'string' ? search.domain : undefined,
    days: typeof search.days === 'number' ? search.days : (typeof search.days === 'string' ? parseInt(search.days, 10) || 14 : 14),
    status: typeof search.status === 'string' ? search.status : undefined,
  }),
});


const reportDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/reports/$reportId",
  component: ReportDetailPage,
});

// Admin route guard - redirects non-admins to dashboard
const adminBeforeLoad = () => {
  const { isAdmin, isLoading } = useAuthStore.getState();
  if (!isLoading && !isAdmin) {
    throw redirect({ to: "/dashboard" });
  }
};

const adminRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/admin",
  component: AdminPage,
  beforeLoad: adminBeforeLoad,
});

const adminOrganizationsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/admin/organizations",
  component: AdminOrganizationsPage,
  beforeLoad: adminBeforeLoad,
});

const adminOrganizationDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/admin/organizations/$id",
  component: AdminOrganizationDetailPage,
  beforeLoad: adminBeforeLoad,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/admin/users",
  component: AdminUsersPage,
  beforeLoad: adminBeforeLoad,
});

const adminUserDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/admin/users/$id",
  component: AdminUserDetailPage,
  beforeLoad: adminBeforeLoad,
});

const checkoutSuccessRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/checkout/success",
  component: CheckoutSuccessPage,
});

export const routeTree = rootRoute.addChildren([
  marketingLayoutRoute.addChildren([
    homeRoute,
    publicToolsRoute,
    spfCheckerRoute,
    dkimCheckerRoute,
    dmarcCheckerRoute,
    emailAuthCheckerRoute,
    websiteSecurityScannerRoute,
    blacklistCheckerRoute,
    pricingRoute,
    resourcesRoute,
    resourceHubRoute,
    blogRoute,
    blogPostRoute,
    termsRoute,
    privacyRoute,
    cookiesRoute,
    aboutRoute,
    guideWhySpamRoute,
    guideEmailAuthRoute,
    guideSpoofingRoute,
    guideSecurityGatewaysRoute,
    guideWebsiteSecurityRoute,
    guideFixingHeadersRoute,
    registerRoute,
    authRoute.addChildren([loginRoute, forgotPasswordRoute, resetPasswordRoute, verifyEmailRoute]),
  ]),
  dashboardLayoutRoute.addChildren([
    dashboardRoute,
    domainsRoute,
    reportsRoute,
    reportDetailRoute,
    dashboardToolsRoute,
    dashboardSecurityRoute,
    settingsRoute,
    teamRoute,
    profileRoute,
    adminRoute,
    adminOrganizationsRoute,
    adminOrganizationDetailRoute,
    adminUsersRoute,
    adminUserDetailRoute,
    checkoutSuccessRoute,
  ]),
]);

// Export routes for components to use useSearch
export { dashboardRoute, reportsRoute };
